"use client"

import { useMemo, useState } from "react";
import { Loader } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom"
import BreadCrumb from "../../../componentsV2/BreadCrumb"
import VehicleDetails from "../../../componentsV2/VehicleDetails"
import { REQUEST_STATUS } from "../../../util/constants";
import { useCurrency } from "../../../hooks/useCurrency";
import dayjs from "dayjs"
import { OrdersType } from "@/src/shared/interfaces/requests.interface";
import graniteImg from "../../../assets/images/granite.png";
import useSWR from "swr"
import { fetcher } from "../../../util/api"

export default function TruckRequestDetails() {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get('vehicleId');
  const {formatCurrency} = useCurrency();

  // Fetch request details
  const { data: requestsData, isLoading } = useSWR(`/renter/rental-request/${requestId}`, fetcher);
  const { data, isLoading: loadingVehicle } = useSWR(`newAdmin/vehicles/${vehicleId}`, fetcher);


  const request: OrdersType = useMemo(() => {
    if (!requestsData) return null;
    return requestsData ?? null;
  }, [requestsData]);


  const vehicle: Vehicle = useMemo(() => {
    return data?.vehicleDetails ?? null;
  }, [data]);


  if (isLoading && !request) {
    return (<div className="h-96 flex items-center justify-center"><Loader /></div>)
  }

  return (
    <main className="min-h-screen bg-primary-accent p-6 h-full">
        <div className="h-full flex flex-col lg:flex-row gap-6">
            {/* Left section - Rental Request list */}
            <div className="flex-1 bg-white  h-full rounded-lg shadow-sm border p-6">
                {/* Back button and title */}
                <BreadCrumb label={`${vehicle?.referenceId ?? 'Request'} - Details`} showBack />
                {/* Transaction Details */}
                <div className="flex flex-col md:flex-row gap-6">
                {/* Rental Request Info */}
                <div className="flex-1 flex flex-col gap-2 min-w-[220px]">
                    <h2 className="font-semibold text-base text-gray-500 my-4">Rental Request Information</h2>
                    <Link to={"#"} className="text-blue-600 font-bold">
                    {request?.invoiceId}
                    </Link>
                    <p className="text-[#F9A825] font-semibold text-lg my-0">{formatCurrency(request?.totalPrice || 0)}</p>
                    <p className="text-[#90A4AE] text-xs my-0">{dayjs(request?.createdAt).format('DD/MM/YYYY')}</p>
                    <div>
                        <img src={graniteImg} alt="Granite" className="w-24 h-8 object-cover rounded mt-2" />
                    </div>
                    <div className="bg-[#F5F5F5] border-2 border-dashed border-blue-200 rounded p-2 mt-4">
                        <div className="font-medium text-[#1A237E] text-sm underline cursor-pointer">
                            {request?.renterName}
                        </div>
                        <div className="text-blue-600 text-sm my-3">{request?.renterPhone}</div>
                        <div className="text-[#90A4AE] text-xs">{request?.renterEmail}</div>
                        <div className="text-[#90A4AE] text-xs mt-1">On {dayjs(request?.createdAt).format('DD/MM/YYYY @ hh:mm')}</div>
                    </div>
                    {request?.status === REQUEST_STATUS.ACTIVE && (
                    <div className="bg-[#F5F5F5] p-2 mt-4 text-sm text-gray-500">
                        <h2 className="my-4 text-sm">Trip Completed</h2>
                        <div className="space-y-2 text-xs">
                        {Array.from({ length: request?.noOfTrips }, (v, i) => i).map((trip, index) => (<div key={index} className="flex items-center">
                            <span>Trip {index + 1}</span>
                            <span className="text-primary ml-auto mr-4">{dayjs(request?.createdAt).format('DD/MM/YYYY @ hh:mm') ?? '-'}</span>
                        </div>))}
                        </div>
                    </div>
                    )}
                    {request.status === REQUEST_STATUS.ASSIGNED && (
                    <div className="bg-[#F5F5F5] p-2 mt-4 text-sm text-gray-500">
                        <h2 className="my-4 text-sm">Trip Completed</h2>
                        <div className="space-y-2 text-xs">
                        {Array.from({ length: request.totalAssignedTrips }, (v, i) => i).map((trip, index) => (<div key={index} className="flex items-center">
                            <span>Trip {index + 1}</span>
                            <span className="text-primary ml-auto mr-4">{dayjs(request.createdAt).format('DD/MM/YYYY @ hh:mm') ?? '-'}</span>
                        </div>))}
                        </div>
                    </div>
                    )}
                    {request?.status === REQUEST_STATUS.ASSIGNED && (
                    <div className="bg-[#F5F5F5] p-2 mt-4 text-sm text-gray-500">
                        <h2 className="my-4 text-sm">Trip Assignment</h2>
                        <div className="space-y-2 text-xs">
                        {request?.assignments.map((assignment, index) => (<div key={index}>
                            <div className="flex items-center justify-between">                                
                                <span>{assignment?.fleetOwner?.companyName}{' '}({assignment?.tripsAssigned})</span>
                                <span className="text-primary mr-4">{dayjs(assignment?.timeOfAssignment).format('DD/MM/YYYY @ hh:mm') ?? '-'}</span>
                            </div>
                            {assignment.vehicles.map((vehicle) => (
                                <div key={vehicle.vehicle.id} className="bg-[#F5F5F5] border-2 border-dashed border-blue-200 rounded p-2 my-3">
                                    <p className="flex items-center justify-between">
                                    <span>Diver Name:</span>
                                    <strong className="text-black font-medium">{vehicle.vehicle.driverName}</strong>
                                    </p>
                                    <p className="flex items-center justify-between">
                                    <span>Diver Phone:</span>
                                    <strong className="text-black font-medium">{vehicle.vehicle.driverPhone}</strong>
                                    </p>
                                    <p className="flex items-center justify-between">
                                    <span>Plate Number:</span>
                                    <strong className="text-black font-medium">{vehicle.vehicle.regPlate}</strong>
                                    </p>
                                    <p className="flex items-center justify-between">
                                    <span>No. Of Trip(s):</span>
                                    <strong className="text-black font-medium">{vehicle.noOfTrips}</strong>
                                    </p>
                                    <p className="flex items-center justify-between">
                                    <span>Completed Trip(s):</span>
                                    <strong className="text-black font-medium">{vehicle.completedTrips}</strong>
                                    </p>
                                </div>
                            ))}
                        </div>))}
                        </div>
                    </div>
                    )}
                </div>
                {/* Route Info */}
                <div className="flex-1 flex flex-col gap-6">
                    <div>
                    <h2 className="font-semibold text-base text-gray-500 my-4">Route</h2>
                    <div className="flex flex-col gap-4">
                        <div>
                        <h4 className="text-sm text-gray-500 mb-2">Loading Point</h4>
                        <div className="bg-[#F5F5F5] rounded-t px-4 py-2 font-semibold text-[#1A237E]">{request?.pickupZone?.title}</div>
                        <div className="bg-[#E3F2FD] rounded-b px-4 py-2 text-[#90A4AE] text-sm">{request?.pickupZone?.description}</div>
                        </div>
                        <div>
                        <h4 className="text-sm text-gray-500 mb-2">Dumping Point</h4>
                        <div className="bg-[#F5F5F5] rounded-t px-4 py-2 font-semibold text-[#1A237E]">{request?.dropoffZone.title}</div>
                        <div className="bg-[#E3F2FD] rounded-b px-4 py-2 text-[#90A4AE] text-sm">{request?.dropoffZone.description}</div>
                        </div>
                    </div>
                    </div>
                </div>
                </div>
                {/* Statuses */}
                <div className="grid grid-cols-3 gap-4 mt-12">
                    <div className="flex">
                        <div>
                            <div className="text-[#90A4AE] font-bold text-xs">Assigned</div>
                            <div className="text-blue-500 font-medium capitalize">
                            {request?.status !== REQUEST_STATUS.PENDING ? request?.assignedBy?.name : 'Pending'}
                            </div>
                            <div className="text-[#90A4AE] text-xs">
                            {request?.assignedBy ? dayjs(request?.assignedBy?.timeOfAssignment).format('DD/MM/YYYY @ hh:mm') : '-'}
                            </div>
                        </div>
                        <div className="flex-1 mt-2 h-[3px] bg-blue-200" />
                    </div>
                    <div className="flex">
                        <div>
                            <div className="text-[#90A4AE] font-bold text-xs">Deployed</div>
                            <div className="text-blue-500 font-medium capitalize">{request?.status === REQUEST_STATUS.ACTIVE ? 'Active' : 'Pending'}</div>
                            <div className="text-[#90A4AE] text-xs">-</div>
                        </div>
                        <div className="flex-1 mt-2 h-[3px] bg-blue-200" />
                    </div>
                    <div>
                        <div className="text-[#90A4AE] font-bold text-xs">Completed</div>
                        <div className="text-blue-500 font-medium capitalize">{request?.status === REQUEST_STATUS.COMPLETED ? 'Completed' : 'Pending'}</div>
                        <div className="text-[#90A4AE] text-xs">-</div>
                    </div>
                </div>
            </div>
            {/* Right section - Vehicle Details */}
            {vehicle && <VehicleDetails vehicle={vehicle} />}
        </div>
    </main>
  )
}